import { PathParam, generatePath } from "react-router-dom";
import { CommitmentType, CostType, Maybe, Stage, TakeoffLineItemFilter } from "src/generated/graphqlTypes";
import {
  bidCommitmentChangeOrderPdfPath,
  bidCommitmentPdfPath,
  billPdfPath,
  commitmentChangeOrderPdfPath,
  commitmentPdfPath,
  costMappingPath,
  developmentCommitmentPdfPath,
  developmentCommitmentsPaths,
  developmentContractPdfPath,
  developmentPaths,
  estimateExportPath,
  finishScheduleVisualModePdfPath,
  homeownerChangeOrderPath,
  invoicePdfPath,
  lienWaiverPdfPath,
  lotSequenceSheetPdfPath,
  lotSummaryDetailsPdfPath,
  personalDashboardPaths,
  productOfferingConfigsComparePath,
  projectFinishSchedulePdfPath,
  projectFinishScheduleVisualModePdfPath,
  projectItemScopeOfWorkPath,
  reassignPurchaseOrdersPaths,
  releaseToTradesPaths,
  scheduleExportPath,
  scheduleOfValuesPath,
  toDoPdfPath,
  tradeScopeOfWorkPath,
  tradeSpecificAgreementPath,
  tradesPunchlistPdfPath,
} from "src/routes/routesDef";
import * as bidPackagesPaths from "src/routing/domains/bidPackagesPaths";
import * as changeLogPaths from "src/routing/domains/changeLogPaths";
import { developmentsRoute } from "src/routing/domains/developmentsPaths";
import * as financesPaths from "src/routing/domains/financesPaths";
import * as librariesPaths from "src/routing/domains/librariesPaths";
import * as projectPaths from "src/routing/domains/projectsPaths";
import { expensesRoute } from "src/routing/domains/rootPaths";
import * as tradePartnersPaths from "src/routing/domains/tradePartnersPaths";
import {
  tradesBidContractsRoute,
  tradesPurchaseOrdersRoute,
  tradesTradePartnersRoute,
} from "src/routing/domains/tradesPaths";
import { warrantyDetailsRoute } from "src/routing/domains/warrantyPaths";
import { getSlugFromStage, maybeTagFromId } from "src/utils";
import { Temporal } from "temporal-polyfill";
import { EnumMapping, mapEnum } from "ts-enum-mapper";
import { baseDownloadUrl } from "./context";
import { fail, maybeDeTagId } from "./utils";

/** Present in place of an ID in URLs when creating items on CRUD pages */
export const addEntityParam = "add";

type CommitmentTypeSlug = "purchase-order" | "subcontract";
/** Maps commitment type to/from query string values. */
const commitmentTypeValues: EnumMapping<typeof CommitmentType, CommitmentTypeSlug> = mapEnum(CommitmentType, {
  PurchaseOrder: "purchase-order",
  Subcontract: "subcontract",
});

export type DocumentsParams = { groupBy?: string; documentId?: string };

export function createProjectToDoModalUrl(projectId: string, id: string) {
  return projectPaths.projectToDoModalRoute.url({ projectId, toDoId: id });
}

export function createDashboardTodoModalUrl(id: string) {
  return generatePath(personalDashboardPaths.toDoModal, { toDoId: id });
}

export function createBudgetUrl(projectId: string, stage?: Stage) {
  if (!stage) {
    return projectPaths.projectBudgetBaseRoute.url({ projectId });
  }
  return projectPaths.projectBudgetRoute.url({ projectId, stageSlug: getSlugFromStage(stage) });
}

export function createBudgetMetricsUrl(projectId: string) {
  return projectPaths.projectBudgetMetricsRoute.url({ projectId });
}

export function createCommitmentsUrl(projectId: string) {
  return projectPaths.projectCommitmentsRoute.url({ projectId });
}

export function createCommitmentUrl(
  projectId: string,
  idOrAdd: string = addEntityParam,
  unformattedType?: CommitmentType,
) {
  const basePath = projectPaths.projectCommitmentRoute.url({ projectId, idOrAdd });

  if (idOrAdd === addEntityParam) {
    if (!unformattedType) {
      fail("A 'type' must be passed when generating a URL for a new commitment");
    }
    const type = commitmentTypeValues.map(unformattedType);
    return `${basePath}?type=${type}`;
  }

  return basePath;
}

export function createCommitmentChangeOrderUrl(
  projectId: string,
  commitmentId: string,
  idOrAdd: string = addEntityParam,
) {
  return projectPaths.projectCommitmentChangeOrderRoute.url({ projectId, commitmentId, idOrAdd });
}

export function createInvoiceTermsUrl() {
  return financesPaths.invoiceTermsRoute.url();
}

export function createBillsAndCreditsPageUrl() {
  return financesPaths.billsRoute.url();
}

export function createBillPageUrl({
  idOrAdd = addEntityParam,
  commitmentLikeId,
  credit,
  edit,
}: { idOrAdd?: string; commitmentLikeId?: string; credit?: boolean; edit?: boolean } = {}) {
  const baseUrl = financesPaths.createBillRoute.url({ idOrAdd });
  const editsuffix = edit ? "&edit=1" : "";

  if (commitmentLikeId) {
    const creditsuffix = credit ? "&credit=1" : "";

    return `${baseUrl}?commitmentLikeId=${commitmentLikeId}${creditsuffix}${editsuffix}`;
  }
  return `${baseUrl}${editsuffix.replace("&", "?")}`;
}

export function createBillUrl(billId: string) {
  return financesPaths.createBillRoute.url({ idOrAdd: billId });
}

export function createHomeownerContractsUrl(projectId: string, contractId?: string) {
  if (contractId) {
    return projectPaths.projectContractRoute.url({ projectId, contractId });
  }
  return projectPaths.projectContractsRoute.url({ projectId });
}

export function createHomeownerContractChangeOrderUrl(projectId: string, changeOrderId: string = addEntityParam) {
  return projectPaths.projectContractChangeOrderRoute.url({ projectId, changeOrderId });
}

export function createHomeownerContractChangeOrderDrawsUrl(projectId: string, changeOrderId: string = addEntityParam) {
  return `${projectPaths.projectContractChangeOrderRoute.url({ projectId, changeOrderId })}?tab=draws`;
}

export function createHomeownerContractDrawsUrl(projectId: string, contractId: string) {
  return `${projectPaths.projectContractRoute.url({ projectId, contractId })}?tab=draws`;
}

export function createProjectUrl(projectId: string): string {
  return projectPaths.projectRoute.url({ projectId });
}

export function createProjectsListUrl() {
  return projectPaths.projectsRoute.url();
}

export function createDevelopmentsUrl() {
  return developmentsRoute.url();
}

export function createDevelopmentUrl(developmentId: string): string {
  return generatePath(developmentPaths.development, { developmentId });
}

export function createDevelopmentLotSchedulesUrl(developmentId: string): string {
  return generatePath(developmentPaths.lotSchedules, { developmentId });
}

export function createDevelopmentScheduleUrl(developmentId: string): string {
  return generatePath(developmentPaths.developmentSchedule, { developmentId });
}

export function createDevelopmentTeamMembersUrl(developmentId: string) {
  return generatePath(developmentPaths.team, { developmentId });
}

export function createDevelopmentDropsUrl(developmentId: string) {
  return generatePath(developmentPaths.drops, { developmentId });
}

export function createDevelopmentJobLogsUrl(developmentId: string): string {
  return generatePath(developmentPaths.jobLogs, { developmentId });
}

export function createDevelopmentJobLogsGalleryUrl(developmentId: string): string {
  return generatePath(developmentPaths.jobLogsGallery, { developmentId });
}

export function createDevelopmentJobLogNoteDrawerUrl(developmentId: string, jobLogId: string, jobLogNoteId: string) {
  return generatePath(developmentPaths.jobLogNoteDrawer, { developmentId, jobLogId, jobLogNoteId });
}

export function createDevelopmentLotSummaryUrl(developmentId: string) {
  return generatePath(developmentPaths.lotSummary, { developmentId });
}

export function createDevelopmentLotSequenceSheetUrl(developmentId: string) {
  return generatePath(developmentPaths.lotSequenceSheet, { developmentId });
}

export function createDevelopmentLotDetailsUrl(developmentId: string, projectReadyPlanConfigId: string) {
  return generatePath(developmentPaths.lotDetails, { developmentId, projectReadyPlanConfigId });
}

export function createDevelopmentLotSummaryReleaseUrl(developmentId: string) {
  return generatePath(developmentPaths.lotSummaryRelease, { developmentId });
}

export function createDevelopmentAllLotsUrl(developmentId: string): string {
  return generatePath(developmentPaths.allLots, { developmentId });
}

export function createDevelopmentProcurementUrl(developmentId: string): string {
  return generatePath(developmentPaths.procurement, { developmentId });
}

export function createDevelopmentPurchaseOrdersUrl(developmentId: string): string {
  return generatePath(developmentPaths.purchaseOrders, { developmentId });
}

export function createDevelopmentReassignPurchaseOrdersSelectUrl(developmentId: string): string {
  return generatePath(reassignPurchaseOrdersPaths.select, { developmentId });
}

export function createDevelopmentReassignPurchaseOrdersReleaseUrl(developmentId: string): string {
  return generatePath(reassignPurchaseOrdersPaths.release, { developmentId });
}

export function createDevelopmentCostMapPageUrl(developmentId: string): string {
  return generatePath(costMappingPath, { developmentId });
}

export function createDevelopmentLotConfigUrl(developmentId: string) {
  return generatePath(developmentPaths.lotConfig, { developmentId });
}

export function createDevelopmentPlanAndOptionsUrl(developmentId: string) {
  return generatePath(developmentPaths.planAndOptions, { developmentId });
}

export function createDevelopmentProductOfferingUrl(developmentId: string, productOfferingId: string, rpavId?: string) {
  return generatePath(developmentPaths.productOffering, { developmentId, productOfferingId, rpavId });
}

export function createProjectProductOfferingUrl(projectId: string, productOfferingId: string, rpavId?: string) {
  return generatePath(projectPaths.projectProductOfferingOptionalPath, { projectId, productOfferingId, rpavId });
}

type DevelopmentOrProjectProductOfferingLink = {
  id: string;
  aggregateLatest: { id: string };
  development?: { id: string } | null;
  project?: { id: string } | null;
};

export function getDevelopmentOrProjectProductOfferingUrl(
  productOffering: DevelopmentOrProjectProductOfferingLink,
): string {
  return productOffering.development
    ? createDevelopmentProductOfferingUrl(
        productOffering.development.id,
        productOffering.id,
        productOffering.aggregateLatest.id,
      )
    : createProjectProductOfferingUrl(
        productOffering.project!.id,
        productOffering.id,
        productOffering.aggregateLatest.id,
      );
}

export function createDevelopmentSettingsUrl(developmentId: string) {
  return generatePath(developmentPaths.settings, { developmentId });
}

export function createProductOfferingScopeUrl(
  productOfferingId: string,
  rpavId: string,
  opts?: Partial<{ search: string; projectId: string; developmentId: string; filter: TakeoffLineItemFilter }>,
) {
  return librariesPaths.productOfferingScopeRoute.url(
    { productOfferingId, rpavId },
    {
      search: opts?.search,
      projectId: opts?.projectId,
      devId: opts?.developmentId,
      filter: opts?.filter ? JSON.stringify(opts.filter) : undefined,
    },
  );
}

/** Creates a URL for the ProductOffering cost calculator page. */
export function createProductOfferingCostCalculatorUrl(productOfferingId: string, rpavId: string) {
  return librariesPaths.productOfferingCostCalculatorRoute.url({ productOfferingId, rpavId });
}

/**
 * Creates a URL for the page that edits a ProductOffering's checkout starting config.
 *
 * The page derives its development/project breadcrumb from the offering itself, so no context
 * search params are needed here.
 */
export function createProductOfferingCheckoutStartingConfigUrl(productOfferingId: string, rpavId: string) {
  return librariesPaths.productOfferingCheckoutStartingConfigRoute.url({ productOfferingId, rpavId });
}

/** Creates a URL for the page that bulk-edits a ProductOffering's underwriting starting configs. */
export function createProductOfferingUwStartingConfigsUrl(productOfferingId: string, rpavId: string) {
  return librariesPaths.productOfferingUwStartingConfigsRoute.url({ productOfferingId, rpavId });
}

export function createProductOfferingUrl(
  idOrAdd: string = addEntityParam,
  rpavId?: string,
  developmentId?: Maybe<string>,
  projectId?: Maybe<string>,
) {
  if (developmentId) {
    return idOrAdd === "add"
      ? librariesPaths.productOfferingRoute.url({ idOrAdd }, { devId: developmentId })
      : generatePath(developmentPaths.productOffering, { productOfferingId: idOrAdd, rpavId, developmentId });
  }
  if (projectId) {
    return idOrAdd === "add"
      ? librariesPaths.productOfferingRoute.url({ idOrAdd }, { projectId })
      : generatePath(projectPaths.projectProductOfferingOptionalPath, {
          productOfferingId: idOrAdd,
          rpavId,
          projectId,
        });
  }
  return librariesPaths.productOfferingRoute.url({ idOrAdd });
}

export function createProductOfferingApplyToProjectsUrl(
  productOfferingId: string,
  rpavId: string,
  developmentId?: string,
  projectId?: string,
) {
  if (developmentId) {
    return generatePath(developmentPaths.productOfferingApplyToProject, { productOfferingId, rpavId, developmentId });
  }
  return projectPaths.projectProductOfferingApplyToProjectRoute.url({
    productOfferingId,
    rpavId,
    projectId: projectId ?? fail("createProductOfferingApplyToProjectsUrl requires developmentId or projectId"),
  });
}

export function createProductOfferingEditUrl(
  productOfferingId: string,
  rpavId: string,
  developmentId?: Maybe<string>,
  projectId?: Maybe<string>,
) {
  return librariesPaths.productOfferingEditRoute.url(
    { productOfferingId, rpavId },
    { devId: developmentId ?? undefined, projectId: projectId ?? undefined },
  );
}

export type CreateBidPackageSearchParams = {
  projectId?: string;
  /** Prefills the cost codes multi-select on the create stepper (e.g. from a Missing cost-code banner). */
  costCodes?: string[];
  /** Prefills the cost type select on the create stepper. */
  costType?: CostType;
};

export function createBidPackageUrl(idOrAdd: string = addEntityParam, params?: CreateBidPackageSearchParams) {
  const pathname = bidPackagesPaths.bidPackageRoute.url({ idOrAdd });
  return `${pathname}${bidPackageCreateSearch(params)}`;
}

export function createBidPackagesEditUrl(bidPackageGroupId: string, projectId?: Maybe<string>) {
  return `${generatePath(bidPackagesPaths.bidPackageEditRoute.path, { bidPackageGroupId })}${projectId ? `?projectId=${projectId}` : ""}`;
}

export function createPlanBasedBidPackageUrl(params?: CreateBidPackageSearchParams) {
  const pathname = generatePath(bidPackagesPaths.planBasedBidPackageCreateRoute.path);
  return `${pathname}${bidPackageCreateSearch(params)}`;
}

export function createPlanBasedBidPackageEditUrl(bidPackageGroupId: string, projectId?: Maybe<string>) {
  return `${generatePath(bidPackagesPaths.planBasedBidPackageEditRoute.path, { bidPackageGroupId })}${projectId ? `?projectId=${projectId}` : ""}`;
}

export function createEnterPlanBasedBidUrl(
  bidPackageId: string,
  bidderId: string,
  search?: { productOfferingId?: string },
) {
  return bidPackagesPaths.enterPlanBasedBidRoute.url({ bidPackageId, bidderId }, search);
}

export function createEnterUnitBasedBidUrl(bidPackageId: string, bidderId: string) {
  return bidPackagesPaths.enterUnitBasedBidRoute.url({ bidPackageId, bidderId });
}

export function createBidPackagesDetailUrl(
  bidPackageGroupId: string,
  bidPackageId: string,
  search?: { productOfferingId?: string },
) {
  return bidPackagesPaths.bidPackageDetailsRoute.url({ bidPackageGroupId, bidPackageId }, search);
}

export function createDevelopmentPlanAndOptionsDetailsUrl(developmentId: string, readyPlanId: string) {
  return generatePath(developmentPaths.planDetails, { developmentId, readyPlanId });
}

export function createDevelopmentPlanAndOptionsDetailsPlanDataUrl(developmentId: string, readyPlanId: string) {
  return generatePath(developmentPaths.planData, { developmentId, readyPlanId });
}

export function createDevelopmentPlanAndOptionsDetailsOptionDataUrl(developmentId: string, readyPlanId: string) {
  return generatePath(developmentPaths.optionData, { developmentId, readyPlanId });
}

export function createDevelopmentPlanAndOptionsDetailsActivityUrl(developmentId: string, readyPlanId: string) {
  return generatePath(developmentPaths.activity, { developmentId, readyPlanId });
}

export function createDevelopmentScopeTemplateUrl(developmentId: string, itemTemplateId: string): string {
  return generatePath(developmentPaths.scopeTemplate, { developmentId, itemTemplateId });
}

export function createDevelopmentReviewTemplatesUrl(developmentId: string) {
  return generatePath(developmentPaths.reviewTemplates, { developmentId });
}

export function createDevelopmentScopeTemplatesUrl(developmentId: string): string {
  return generatePath(developmentPaths.scopeTemplates, { developmentId });
}

export function createDevelopmentScopeTemplateCostReportUrl(developmentId: string, itemTemplateId: string): string {
  return generatePath(developmentPaths.scopeTemplateCostReport, { developmentId, itemTemplateId });
}

export function createDevelopmentScopeTemplateItemUrl(
  developmentId: string,
  itemTemplateId: string,
  itemTemplateItemId: string,
): string {
  return generatePath(developmentPaths.scopeTemplateItem, { developmentId, itemTemplateId, itemTemplateItemId });
}

export function createProjectBidContractsUrl(projectId: string): string {
  return projectPaths.projectBidContractsRoute.url({ projectId });
}

export function createProjectBidContractOverviewUrl(projectId: string, bidContractRevisionId: string): string {
  return projectPaths.projectBidContractOverviewRoute.url({ projectId, bidContractRevisionId });
}

export function createProjectBidContractPlanPricingUrl(projectId: string, bidContractRevisionId: string): string {
  return projectPaths.projectBidContractPlanPricingRoute.url({ projectId, bidContractRevisionId });
}

export function createProjectBidContractUnitPricingUrl(projectId: string, bidContractRevisionId: string): string {
  return projectPaths.projectBidContractUnitPricingRoute.url({ projectId, bidContractRevisionId });
}

export function createProjectBidContractPurchaseOrdersUrl(projectId: string, bidContractRevisionId: string): string {
  return projectPaths.projectBidContractPurchaseOrdersRoute.url({ projectId, bidContractRevisionId });
}

export function createProjectDropsUrl(projectId: string) {
  return projectPaths.projectDropsRoute.url({ projectId });
}

export function createDevelopmentContractUrl(developmentId: string, bidContractRevisionId: string): string {
  return generatePath(developmentPaths.contract, { developmentId, bidContractRevisionId });
}

export function createDevelopmentContractOverviewUrl(developmentId: string, bidContractRevisionId: string): string {
  return generatePath(developmentPaths.contractOverview, { developmentId, bidContractRevisionId });
}

export function createDevelopmentContractLineItemsUrl(developmentId: string, bidContractRevisionId: string): string {
  return generatePath(developmentPaths.contractLineItems, { developmentId, bidContractRevisionId });
}

export function createDevelopmentContractUnitPricingUrl(developmentId: string, bidContractRevisionId: string): string {
  return generatePath(developmentPaths.unitPricing, { developmentId, bidContractRevisionId });
}

export function createDevelopmentContractPurchaseOrdersUrl(
  developmentId: string,
  bidContractRevisionId: string,
): string {
  return generatePath(developmentPaths.contractPurchaseOrders, { developmentId, bidContractRevisionId });
}

export function createDevelopmentTaskLookaheadReportUrl(developmentId: string): string {
  return generatePath(developmentPaths.taskLookaheadReport, { developmentId });
}

export function createPlanDetailsUrl(developmentId: string, readyPlanId: string): string {
  return generatePath(developmentPaths.planDetails, { developmentId, readyPlanId });
}

export function createReadyPlanEditOptionsUrl(developmentId: string, readyPlanId: string): string {
  return generatePath(developmentPaths.editOptions, { developmentId, readyPlanId });
}

export function createGlobalOptionsUrl() {
  return librariesPaths.globalOptionsRoute.url();
}

export function createGlobalOptionsLegacyUrl() {
  return librariesPaths.globalOptionsLegacyRoute.url();
}

export function createGlobalOptionsFormUrl(idOrAdd: string = addEntityParam) {
  return librariesPaths.globalOptionRoute.url({ idOrAdd });
}

export function createMaterialCatalogUrl() {
  return librariesPaths.materialsCatalogRoute.url();
}

export function createMaterialPageUrl() {
  return librariesPaths.materialCreateRoute.url();
}

export function createMaterialMergeUrl() {
  return librariesPaths.materialMergeRoute.url();
}

/** AI import wizard URL: `/libraries/material-catalog/import`, or `...?proposal=ap:1` to resume a running import. */
export function createMaterialImportUrl(proposalId?: string) {
  return librariesPaths.materialImportRoute.url(proposalId ? { proposal: proposalId } : undefined);
}

/** Listing detail URL: `/libraries/material-catalog/ml:2?mv=mv:49` or `...?createNew=1`. */
export function createMaterialDetailsUrl(listingId: string, opts?: { mv: string } | { createNew: true }) {
  if (opts && "createNew" in opts) {
    return librariesPaths.materialDetailsRoute.url({ listingId }, { createNew: "1" });
  }
  return librariesPaths.materialDetailsRoute.url({ listingId }, opts?.mv ? { mv: opts.mv } : undefined);
}

export function createMaterialCostUrl(variantId: string) {
  return librariesPaths.materialCostRoute.url({ variantId });
}

export function createInstallTasksUrl() {
  return librariesPaths.installTasksRoute.url();
}

export function createPurchaseOrdersUrl() {
  return financesPaths.purchaseOrdersRoute.url();
}

export function createItemCatalogUrl() {
  return librariesPaths.itemCatalogRoute.url();
}

export function createBidItemPageUrl() {
  return librariesPaths.bidItemCreateRoute.url();
}

export function createBidItemDetailsUrl(bidItemId: string) {
  return librariesPaths.bidItemDetailsRoute.url({ bidItemId });
}

export function createProjectScheduleUrl(projectId: string, stage?: Stage, taskId?: string): string {
  if (!stage) {
    return projectPaths.projectScheduleBaseRoute.url({ projectId });
  }

  const path = projectPaths.projectScheduleRoute.url({ projectId, stageSlug: getSlugFromStage(stage) });
  if (taskId) {
    return `${path}?taskId=${taskId}`;
  }
  return path;
}

export function createTeamMembersUrl(projectId: string) {
  return projectPaths.projectTeamRoute.url({ projectId });
}

export function createToDosPdfUrl(toDoIds: string[]) {
  return generateUrlWithQueries(toDoPdfPath, { toDoIds });
}

export function createFinishSchedulVisualModePdfUrl(params: Record<string, string | string[]>) {
  return generateUrlWithQueries(finishScheduleVisualModePdfPath, params);
}

export function createProjectFinishSchedulVisualModePdfUrl(params: Record<string, string | string[]>) {
  return generateUrlWithQueries(projectFinishScheduleVisualModePdfPath, params);
}

export function createProjectFinishSchedulPdfUrl(params: Record<string, string | string[]>) {
  return generateUrlWithQueries(projectFinishSchedulePdfPath, params);
}

export function createTradesPunchListPdfUrl(params: Record<string, string | string[]>) {
  return generateUrlWithQueries(tradesPunchlistPdfPath, params);
}

export function createCommitmentPdfPreviewUrl(commitmentId: string) {
  return `${baseDownloadUrl()}/pdf?type=commitmentPdf&commitmentId=${encodeURIComponent(commitmentId)}`;
}

export function createCommitmentChangeOrderPdfPreviewUrl(commitmentChangeOrderId: string) {
  return `${baseDownloadUrl()}/pdf?type=commitmentChangeOrderPdf&commitmentChangeOrderId=${encodeURIComponent(
    commitmentChangeOrderId,
  )}`;
}

type TradePartnerPageTabs =
  "details" | "contacts" | "commitments" | "development-contracts" | "reviews" | "requirements";

const tradePartnerTabRoutes = {
  details: tradePartnersPaths.tradePartnerDetailsRoute,
  contacts: tradePartnersPaths.tradePartnerContactsRoute,
  commitments: tradePartnersPaths.tradePartnerCommitmentsRoute,
  "development-contracts": tradePartnersPaths.tradePartnerDevelopmentContractsRoute,
  reviews: tradePartnersPaths.tradePartnerReviewsRoute,
  requirements: tradePartnersPaths.tradePartnerRequirementsRoute,
} as const;

export function createTradePartnerUrl(idOrAdd = addEntityParam, tab?: TradePartnerPageTabs) {
  if (!tab) return tradePartnersPaths.tradePartnerRoute.url({ idOrAdd });
  return tradePartnerTabRoutes[tab].url({ idOrAdd });
}

export function createTradePartnersUrl() {
  return tradePartnersPaths.tradePartnersRoute.url();
}

type ProjectSettingsTab = "invoices" | "setup" | "teamMembers" | "homeowners" | "schedule" | "checkoutSettings";
export function createProjectSettingsUrl(projectId: string, tab: ProjectSettingsTab | null = null) {
  return `${projectPaths.projectSettingsRoute.url({ projectId })}${tab ? `/${tab}` : ""}`;
}

/**
 * Legacy helper which generates URLs for the new /pre-con-services and /specs-and-selection paths.
 * This reduces refactoring required for generic specs and selections components which accepts a stage as a prop:
 * - SpecsAndSelectionsPage
 * - SpecsAndSelectionsTable
 */
export function createProjectSelectionsUrl(projectId: string, stage: Stage, projectItemId?: string) {
  if (stage === Stage.PreConExecution || stage === Stage.PreConPlanning) {
    if (!projectItemId) return createPreConServicesUrl(projectId);
    return createPreConServicesDetailUrl(projectId, projectItemId);
  } else if (stage === Stage.Construction) {
    if (!projectItemId) return createSpecsAndSelectionsUrl(projectId);
    return createSpecsAndSelectionsDetailUrl(projectId, projectItemId);
  }

  throw new Error(`createProjectSelectionUrl cannot create route for stage ${stage}`);
}

/**
 * Legacy helper which generates URLs for the new /pre-con-services/:projectItemId
 * and /specs-and-selection/:projectItemId paths.
 * This reduces refactoring required for generic specs and selections components which accepts a stage as a prop:
 * - SpecsAndSelectionsPage
 * - SpecsAndSelectionsTable
 */
export function createProjectSelectionDetailsUrl(projectId: string, stage: Stage, projectItemId: string) {
  return createProjectSelectionsUrl(projectId, stage, projectItemId);
}

export function createPreConServicesUrl(projectId: string) {
  return projectPaths.projectPreConServicesRoute.url({ projectId });
}

export function createPreConServicesDetailUrl(projectId: string, projectItemId: string) {
  return projectPaths.projectPreConServicesDetailRoute.url({ projectId, projectItemId });
}

export function createProjectLotSummaryUrl(projectId: string) {
  return projectPaths.projectLotSummaryRoute.url({ projectId });
}

export function createSpecsAndSelectionsUrl(projectId: string) {
  return projectPaths.projectSpecsAndSelectionsRoute.url({ projectId });
}

export function createSpecsAndSelectionsDetailUrl(projectId: string, projectItemId: string) {
  return projectPaths.projectSpecsAndSelectionsDetailRoute.url({ projectId, projectItemId });
}

export function createInvoicesUrl(projectId: string) {
  return projectPaths.projectInvoicesRoute.url({ projectId });
}

export function createInvoiceUrl(projectId: string, idOrAdd: string = addEntityParam, stage?: Stage) {
  const path = projectPaths.projectInvoiceRoute.url({ projectId, idOrAdd });
  if (stage) {
    return `${path}?stageSlug=${getSlugFromStage(stage)}`;
  }

  return path;
}

export function createEstimatesUrl(projectId: string) {
  return projectPaths.projectEstimatesRoute.url({ projectId });
}

// Unlike most CRUD screens, we don't create estimates via the `fooIdOrAdd` convention
// because they're created from a dedicated modal on the specs & selections screen.
export function createEstimateUrl(projectId: string, estimateId: string) {
  return projectPaths.projectEstimateRoute.url({ projectId, estimateId });
}

export function createIndexExpensesUrl() {
  return expensesRoute.url();
}

export function createProjectExpensesUrl(projectId: string) {
  return projectPaths.projectExpensesRoute.url({ projectId });
}

export function createProjectToDosUrl(projectId: string) {
  return projectPaths.projectToDosRoute.url({ projectId });
}

export function createProjectJobLogsUrl(projectId: string) {
  return projectPaths.projectJobLogsRoute.url({ projectId });
}

export function createProjectJobLogsGalleryUrl(projectId: string) {
  return projectPaths.projectJobLogsGalleryRoute.url({ projectId });
}

export function createProjectJobLogNoteDrawerUrl(projectId: string, jobLogId: string, jobLogNoteId: string) {
  return projectPaths.projectJobLogNoteDrawerRoute.url({ projectId, jobLogId, jobLogNoteId });
}

export function createProjectHomeownerNotesUrl(projectId: string, homeownerNoteId?: string) {
  const homeownerNotesPath = projectPaths.projectHomeownerNotesRoute.url({ projectId });
  return homeownerNoteId ? `${homeownerNotesPath}?homeownerNoteId=${homeownerNoteId}` : homeownerNotesPath;
}

export function createDocumentsUrl(parentId: string, opts: { versionId?: string } & DocumentsParams = {}) {
  const { versionId, ...params } = opts;
  const tag = maybeTagFromId(parentId);
  let url: string;
  if (tag === "rp") {
    url = librariesPaths.planPackageDocumentsRoute.url({
      planPackageId: parentId,
      planPackageVersionId: versionId ?? fail("versionId is required"),
    });
  } else if (tag === "dev") {
    url = generatePath(developmentPaths.documents, { developmentId: parentId });
  } else if (tag === "p") {
    url = projectPaths.projectDocumentsRoute.url({ projectId: parentId });
  } else {
    fail(`createDocumentsUrl: unknown tag from id ${parentId}`);
  }
  return params.toKeys().nonEmpty ? `${url}?${toParams(params).toString()}` : url;
}

export function createChangeEventsUrl(projectId: string) {
  return projectPaths.projectChangeEventsRoute.url({ projectId });
}

export function createChangeEventUrl(projectId: string, changeEventId: string) {
  return projectPaths.projectChangeEventRoute.url({ projectId, changeEventId });
}

export function createGuidedChangeEventUrl(projectId: string, changeEventId: string) {
  return projectPaths.projectGuidedChangeEventRoute.url({ projectId, idOrAdd: changeEventId });
}

export function createAddGuidedChangeEventUrl(projectId: string) {
  return projectPaths.projectGuidedChangeEventRoute.url({ projectId, idOrAdd: addEntityParam });
}

export function createChangeEventLineItemsUrl(projectId: string, changeEventId: string) {
  return projectPaths.projectChangeEventLineItemsRoute.url({ projectId, changeEventId });
}

export function createChangeEventOverviewUrl(projectId: string, changeEventId: string) {
  return projectPaths.projectChangeEventOverviewRoute.url({ projectId, changeEventId });
}

export function createChangeEventEstimatesUrl(projectId: string, changeEventId: string) {
  return projectPaths.projectChangeEventEstimatesRoute.url({ projectId, changeEventId });
}

export function createChangeEventCommitmentsUrl(projectId: string, changeEventId: string) {
  return projectPaths.projectChangeEventCommitmentsRoute.url({ projectId, changeEventId });
}

export function createChangeEventHistoryUrl(projectId: string, changeEventId: string) {
  return projectPaths.projectChangeEventHistoryRoute.url({ projectId, changeEventId });
}

export function createChangeEventRposUrl(projectId: string, changeEventId: string) {
  return projectPaths.projectChangeEventRposRoute.url({ projectId, changeEventId });
}

export function createGlobalChangeLogUrl() {
  return changeLogPaths.changeLogRoute.url();
}

export function createGlobalChangeLogDetailsUrl(changeRequestId: string) {
  return changeLogPaths.changeLogDetailsRoute.url({ changeRequestId });
}

export function createProjectChangeLogUrl(projectId: string) {
  return projectPaths.projectChangeLogRoute.url({ projectId });
}

export function createProjectChangeLogDetailsUrl(projectId: string, changeRequestId: string) {
  return projectPaths.projectChangeLogDetailsRoute.url({ projectId, changeRequestId });
}

export function createDevelopmentChangeLogUrl(developmentId: string) {
  return generatePath(developmentPaths.changeLog, { developmentId });
}

export function createDevelopmentChangeLogDetailsUrl(developmentId: string, changeRequestId: string) {
  return generatePath(developmentPaths.changeLogDetails, { developmentId, changeRequestId });
}

export function createBusinessFunctionsUrl() {
  return changeLogPaths.businessFunctionsRoute.url();
}

export function createChangeTypesUrl() {
  return changeLogPaths.changeTypesRoute.url();
}

export function createBusinessFunctionDetailsUrl(businessFunctionId: string) {
  return changeLogPaths.businessFunctionDetailsRoute.url({ businessFunctionId });
}

function createUrlWithUntaggedIds(path: string, params: Record<string, string | string[]>) {
  // Since we may be using potentially hundreds of ids, we need to save all the space we can in our urls
  // so as not to exceed browser length limitations. As such, we need to strip out tags from ids and use our own
  // formatting for the query string instead of using `use-query-params`'s formatting.
  const queryString = Object.entries(params)
    .map(([key, ids]) => `${key}=${(Array.isArray(ids) ? ids : [ids]).map(maybeDeTagId).join(",")}`)
    .join("&");
  return `${path}?${queryString}`;
}

/** Generates a path with queries for the given object with safe de-tagging Id handling */
function generateUrlWithQueries(path: string, params: Record<string, string | string[] | undefined>) {
  const queryString = Object.entries(params)
    .map(([key, ids]) => {
      if (Array.isArray(ids)) {
        // Improved URL array notation to match use-query-params acceptable pattern of
        // ?filters=a&filters=b&filters=c
        // https://www.npmjs.com/package/use-query-params
        return ids.map((id) => `${key}=${maybeDeTagId(id)}`).join("&");
      } else if (ids) {
        return `${key}=${maybeDeTagId(ids)}`;
      }
      return ids;
    })
    // Filtering undefined values
    .filter((query) => !!query)
    .join("&");
  return `${path}?${queryString}`;
}

export function createProjectItemScopeOfWorkUrl(projectItemIds: string[]) {
  return createUrlWithUntaggedIds(projectItemScopeOfWorkPath, { projectItemIds });
}

export function createTradeScopeOfWorkUrl(changeEventLineItemIds: string[]) {
  return createUrlWithUntaggedIds(tradeScopeOfWorkPath, { changeEventLineItemIds });
}

export function createScheduleOfValuesUrl(projectItemIds: string[]) {
  return createUrlWithUntaggedIds(scheduleOfValuesPath, { projectItemIds });
}

export function createEstimateExportUrl(estimateId: string) {
  return createUrlWithUntaggedIds(estimateExportPath, { estimate: [estimateId] });
}

export function createChangeOrderExhibitUrl(estimateId: string) {
  return createUrlWithUntaggedIds(homeownerChangeOrderPath, { estimateId: [estimateId] });
}

export function createReleaseToTradesConfirmUrl(projectId: string) {
  return generatePath(releaseToTradesPaths.confirm, { projectId });
}

export function createReleaseToTradesReviewUrl(projectId: string) {
  return generatePath(releaseToTradesPaths.review, { projectId });
}

/**
 * Generates a URL that includes the start and end date of a schedule export
 * with optional traderPartners or activities to include in the schedule.
 */
export function createScheduleExportUrl(
  scheduleId: string,
  startDate: Temporal.PlainDate,
  endDate: Temporal.PlainDate,
  tradePartnerIds?: string[],
  scheduleSubPhaseIds?: string[],
  withGantt: boolean = false,
) {
  return generateUrlWithQueries(generatePath(scheduleExportPath, { scheduleId }), {
    startDate: startDate.toString(),
    endDate: endDate.toString(),
    tradePartnerIds,
    scheduleSubPhaseIds,
    withGantt: withGantt ? "1" : "0",
    limit: "2000",
  });
}

export function createProjectFinishScheduleUrl(projectId: string) {
  return projectPaths.projectFinishScheduleRoute.url({ projectId });
}

export function createTradeSpecificAgreementUrl(costDivisionId: string, tradePartnerId: string) {
  return (
    generatePath(tradeSpecificAgreementPath) + `?costDivisionId=${costDivisionId}&tradePartnerId=${tradePartnerId}`
  );
}

export function createDevelopmentCommitmentPdfUrl(developmentCommitmentId: string) {
  return generatePath(developmentCommitmentPdfPath) + `?developmentCommitmentId=${developmentCommitmentId}`;
}

export function createCommitmentPdfUrl(commitmentId: string) {
  return generatePath(commitmentPdfPath) + `?commitmentId=${commitmentId}`;
}

export function createInvoicePdfUrl(invoiceId: string) {
  return generatePath(invoicePdfPath) + `?invoiceId=${invoiceId}`;
}

export function createLienWaiverPdf(lienWaiverId: string) {
  return generatePath(lienWaiverPdfPath) + `?lienWaiverId=${lienWaiverId}`;
}

export function createDevelopmentCommitmentsUrl() {
  return generatePath(developmentCommitmentsPaths.developmentCommitments);
}

export function createDevelopmentCommitmentUrl(developmentCommitmentId: string) {
  return generatePath(developmentCommitmentsPaths.developmentCommitment, { developmentCommitmentId });
}

export function createCommitmentChangeOrderPdfUrl(commitmentChangeOrderId: string) {
  return generatePath(commitmentChangeOrderPdfPath) + `?commitmentChangeOrderId=${commitmentChangeOrderId}`;
}

export function createDevelopmentContractPdfUrl(bidContractId: string) {
  return generatePath(developmentContractPdfPath) + `?bidContractId=${bidContractId}`;
}

export function createBidCommitmentPdfUrl(bidCommitmentId: string) {
  return generatePath(bidCommitmentPdfPath) + `?bidCommitmentId=${bidCommitmentId}`;
}

export function createBidCommitmentChangeOrderPdfUrl(bidCommitmentChangeOrderId: string) {
  return generatePath(bidCommitmentChangeOrderPdfPath) + `?bidCommitmentChangeOrderId=${bidCommitmentChangeOrderId}`;
}

export function createBillPdfUrl(billId: string) {
  return generatePath(billPdfPath) + `?billId=${billId}`;
}

export function createScheduleTemplateUrl(scheduleTemplateId = addEntityParam) {
  return librariesPaths.scheduleTemplateRoute.url({ scheduleTemplateId });
}

export function createScheduleTemplatesUrl() {
  return librariesPaths.scheduleTemplatesRoute.url();
}

export function createLotSequenceSheetPdfUrl(developmentId: string, filter: string | null) {
  const path = `${lotSequenceSheetPdfPath}?developmentId=${developmentId}`;
  return filter ? `${path}&filter=${filter}` : path;
}

export function createLotSummaryDetailsPdfPath(projectReadyPlanConfigId: string): string {
  return lotSummaryDetailsPdfPath + `?projectReadyPlanConfigId=${projectReadyPlanConfigId}`;
}

type DashboardTab = "financials" | "activity" | "pre-con-expectations";
export function createProjectDashboardUrl(projectId: string, tab: DashboardTab | null = null) {
  return `${projectPaths.projectDashboardRoute.url({ projectId })}${tab ? `/${tab}` : ""}`;
}

export function createWarrantyTicketDetailsUrl(warrantyTicketId: string, tab: string | null = null) {
  return warrantyDetailsRoute.url({ warrantyTicketId }, tab ? { tab } : undefined);
}

export function createApprovalDashboardUrl(approvalId: string, tab: string | null = null) {
  return personalDashboardPaths.approvals + `?approvalId=${approvalId}`;
}

export function createTaskCatalogUrl() {
  return librariesPaths.catalogTasksRoute.url();
}

export function createTaskCatalogFormUrl(idOrAdd: string = addEntityParam, copyFromTaskId?: string) {
  if (copyFromTaskId) {
    return librariesPaths.catalogTaskRoute.url({ idOrAdd }, { copyFromTaskId });
  }
  return librariesPaths.catalogTaskRoute.url({ idOrAdd });
}

export function createTaskCatalogLaborCostsPageUrl(globalPlanTaskId: string) {
  return librariesPaths.catalogTaskLaborCostsRoute.url({ globalPlanTaskId });
}

export function createDesignPackagesUrl() {
  return librariesPaths.designPackagesRoute.url();
}

/** Links to the design package configurator, requires versionId to make sure callers don't forget it. */
export function createDesignPackageUrl(designPackageId: string, versionId: string, acceptUpdates: boolean = false) {
  return librariesPaths.designPackageRoute.url(
    { designPackageId, designPackageVersionId: versionId },
    acceptUpdates ? { acceptUpdates: "true" } : undefined,
  );
}

export function createDesignPackageFinishScheduleUrl(designPackageId: string, designPackageVersionId: string) {
  return librariesPaths.designPackageFinishScheduleRoute.url({ designPackageId, designPackageVersionId });
}

export function createDesignPackageCompareModeUrl(designPackageId: string, designPackageVersionId: string) {
  return librariesPaths.designPackageCompareModeRoute.url({ designPackageId, designPackageVersionId });
}

export function createDesignPackageAddToSlotUrl(designPackageId: string, designPackageVersionId: string) {
  return librariesPaths.designPackageAddToSlotRoute.url({ designPackageId, designPackageVersionId });
}

export function createDesignPackageAddProductUrl(
  designPackageId: string,
  designPackageVersionId: string,
  placeholderTliId: string,
  productTliId: string,
) {
  return generatePath(librariesPaths.designPackageAddProductPath, {
    designPackageId,
    designPackageVersionId,
    placeholderTliId,
    productTliId,
  });
}

export function createDesignPackageProductCardUrl(
  designPackageId: string,
  designPackageVersionId: string,
  placeholderTliId: string,
  productTliId?: string,
  materialVariantId?: string | "noMaterialVariant",
) {
  return generatePathWithQueryStrings(
    librariesPaths.designPackageProductCardPath,
    { designPackageId, designPackageVersionId, placeholderTliId, productTliId: productTliId ?? null },
    { materialVariantId },
  );
}

export function createDesignPackageProductSearchUrl(
  designPackageId: string,
  designPackageVersionId: string,
  placeholderTliId: string,
  productTliId?: string,
) {
  return generatePath(librariesPaths.designPackageProductSearchPath, {
    designPackageId,
    designPackageVersionId,
    placeholderTliId,
    productTliId: productTliId ?? null,
  });
}

export function createDesignPackageVersionHistoryUrl(designPackageId: string, designPackageVersionId: string) {
  return librariesPaths.designPackageVersionHistoryRoute.url({ designPackageId, designPackageVersionId });
}

export function createDesignPackagePersonalizationGroupsUrl(designPackageId: string, designPackageVersionId: string) {
  return librariesPaths.designPackagePersonalizationGroupsRoute.url({ designPackageId, designPackageVersionId });
}

export function createDesignPackageRequiredOptionsUrl(designPackageId: string, designPackageVersionId: string) {
  return librariesPaths.designPackageRequiredOptionsRoute.url({ designPackageId, designPackageVersionId });
}

export function createEditPersonalizationOptionUrl(
  designPackageId: string,
  designPackageVersionId: string,
  rpogId: string | "add",
  /** When Adding a new RPOG, this will be first TLI it gets attached to, i.e. the first `rpog.placeholderIds=[tli]` */
  forTliId?: string,
  /** The URL to return to after save/cancel, preserving the user's place */
  returnTo?: string,
) {
  let route = librariesPaths.designPackageEditOptionRoute.url({
    designPackageId,
    designPackageVersionId,
    idOrAdd: rpogId,
  });
  const params = new URLSearchParams();
  if (forTliId) params.set("forTli", forTliId);
  if (returnTo) params.set("returnTo", returnTo);
  if (params.size) route += `?${params.toString()}`;
  return route;
}

export function createTaskCatalogFolderUrl(folderId: string) {
  return librariesPaths.catalogTaskFolderRoute.url({ folderId });
}

export function createPlanPackagesUrl() {
  return librariesPaths.planListRoute.url();
}

export function createPlanPackageUrl(planPackageId: string, planPackageVersionId: string): string {
  return librariesPaths.planPackageRoute.url({ planPackageId, planPackageVersionId });
}

export function createPlanPackageTakeoffUrl(planPackageId: string, planPackageVersionId: string) {
  return librariesPaths.planTakeoffsRoute.url({ planPackageId, planPackageVersionId });
}

export function createPlanPackageIncomingSlotReviewUrl(planPackageId: string, planPackageVersionId: string) {
  return librariesPaths.planIncomingSlotReviewRoute.url({ planPackageId, planPackageVersionId });
}

export function createPlanPackageVersionHistoryUrl(planPackageId: string, planPackageVersionId: string) {
  return librariesPaths.planPackageVersionHistoryRoute.url({ planPackageId, planPackageVersionId });
}

export function createConstraintItemCatalogUrl() {
  return librariesPaths.constraintItemsCatalogRoute.url();
}

export function createTaskDetailsPageUrl(projectId: string, planTaskId: string, query?: { scrollIntoView?: string }) {
  const { scrollIntoView } = query ?? {};
  const queryString = scrollIntoView ? `?scrollIntoView=${scrollIntoView}` : "";
  return `${projectPaths.projectDynamicScheduleTaskRoute.url({ projectId, planTaskId })}${queryString}`;
}

export function createTaskMaterialsPageUrl(projectId: string, planTaskId: string) {
  return `${projectPaths.projectDynamicScheduleTaskMaterialsRoute.url({ projectId, planTaskId })}`;
}

export function createMilestoneDetailsPageUrl(projectId: string, planMilestoneId: string) {
  return `${projectPaths.projectDynamicScheduleMilestoneRoute.url({ projectId, planMilestoneId })}`;
}

export function createDraftScheduleUrl(projectId: string, paneTaskId?: string) {
  const path = projectPaths.projectDraftScheduleRoute.url({ projectId });
  return paneTaskId ? `${path}?paneTaskId=${paneTaskId}` : path;
}

export function createDraftScheduleNewTaskUrl(projectId: string) {
  return projectPaths.projectDraftScheduleNewTaskRoute.url({ projectId });
}

export function createDraftSchedulePublishUrl(projectId: string) {
  return projectPaths.projectDraftSchedulePublishRoute.url({ projectId });
}

export function createDraftCalendarUrl(projectId: string) {
  return projectPaths.projectDraftScheduleCalendarRoute.url({ projectId });
}

export function createDraftMilestoneUrl(projectId: string) {
  return projectPaths.projectDraftScheduleMilestoneRoute.url({ projectId });
}

export function createDraftGanttUrl(projectId: string) {
  return projectPaths.projectDraftScheduleGanttRoute.url({ projectId });
}

export function createTradesTradePartnersUrl() {
  return tradesTradePartnersRoute.url();
}

export function createTradesPurchaseOrdersUrl() {
  return tradesPurchaseOrdersRoute.url();
}

export function createTradesBidContractsUrl() {
  return tradesBidContractsRoute.url();
}

export function createBidPackagesUrl(projectId?: Maybe<string>) {
  if (projectId) {
    return projectPaths.projectBidPackagesRoute.url({ projectId });
  }
  return bidPackagesPaths.bidPackagesRoute.url();
}

export function createBidPackagesV2Url(projectId: string) {
  return projectPaths.projectBidPackagesV2Route.url({ projectId });
}

export function createProjectCostAnalysisUrl(projectId: string) {
  return projectPaths.projectCostAnalysisRoute.url({ projectId });
}

export function createProjectCostAnalysisComparisonUrl(
  projectId: string,
  search?: { baseline?: string; fixed?: Record<string, string> },
) {
  const fixed = search?.fixed && Object.keys(search.fixed).nonEmpty ? JSON.stringify(search.fixed) : undefined;
  return projectPaths.projectCostAnalysisComparisonRoute.url({ projectId }, { baseline: search?.baseline, fixed });
}

export function createProjectCostAnalysisOptionsUrl(projectId: string) {
  return projectPaths.projectCostAnalysisOptionsRoute.url({ projectId });
}

export function createMilestoneCatalogUrl() {
  return librariesPaths.milestonesCatalogRoute.url();
}

export function createMilestoneCatalogFormUrl(idOrAdd: string = addEntityParam) {
  return librariesPaths.milestoneCatalogRoute.url({ idOrAdd });
}

export function createProductOfferingsUrl(developmentId?: Maybe<string>, projectId?: Maybe<string>) {
  return developmentId
    ? generatePath(developmentPaths.productOfferings, { developmentId })
    : projectId
      ? projectPaths.projectProductOfferingsRoute.url({ projectId })
      : librariesPaths.globalProductOfferingsRoute.url();
}

export function createProductOfferingConfigEditUrl(params: {
  productOfferingConfigId: string;
  productOfferingId: string;
  developmentId?: string;
  projectId?: string;
  configIds?: string[];
}) {
  const { developmentId, productOfferingConfigId, productOfferingId, configIds, projectId } = params;
  // support returning to the compare page with the same configs selected after editing a config
  return librariesPaths.productOfferingConfigEditRoute.url(
    { productOfferingId, productOfferingConfigId },
    {
      ...(developmentId ? { devId: developmentId } : projectId ? { projectId } : {}),
      ...(configIds ? { configIds: JSON.stringify(configIds) } : {}),
    },
  );
}

export function createProductOfferingConfigUrl(params: {
  idOrAdd: string;
  productOfferingId: string;
  developmentId?: string;
  projectId?: string;
}) {
  const { developmentId, projectId, idOrAdd = addEntityParam, productOfferingId } = params;
  return librariesPaths.productOfferingConfigRoute.url(
    { productOfferingId, idOrAdd },
    { ...(developmentId ? { devId: developmentId } : {}), ...(projectId ? { projectId } : {}) },
  );
}

export function createProductOfferingConfigsUrl(
  developmentId?: Maybe<string>,
  projectId?: Maybe<string>,
  productOfferingId?: string,
  rpavId?: string,
) {
  let path: string;
  if (developmentId) {
    path = productOfferingId
      ? generatePath(developmentPaths.productOfferingSpecificConfigs, { developmentId, productOfferingId })
      : generatePath(developmentPaths.productOfferingConfigs, { developmentId });
  } else if (projectId) {
    path = productOfferingId
      ? projectPaths.projectProductOfferingSpecificConfigsRoute.url({ projectId, productOfferingId })
      : projectPaths.projectProductOfferingConfigsRoute.url({ projectId });
  } else {
    path = librariesPaths.globalProductOfferingConfigsRoute.url();
  }
  return rpavId ? `${path}?rpavId=${rpavId}` : path;
}

export function createProductOfferingConfigsCompareUrl(
  configIds: string[],
  productOfferingId?: string,
  developmentId?: Maybe<string>,
  projectId?: Maybe<string>,
) {
  const params = toParams({ configIds: JSON.stringify(configIds) }); // --> ?configIds=["poc:1","poc:2"]
  if (developmentId) {
    return productOfferingId
      ? `${generatePath(developmentPaths.productOfferingSpecificConfigsCompare, {
          productOfferingId,
          developmentId,
        })}?${params}`
      : `${generatePath(developmentPaths.productOfferingConfigsCompare, { developmentId })}?${params}`;
  }
  if (projectId) {
    return productOfferingId
      ? `${projectPaths.projectProductOfferingSpecificConfigsCompareRoute.url({ productOfferingId, projectId })}?${params}`
      : `${projectPaths.projectProductOfferingConfigsCompareRoute.url({ projectId })}?${params}`;
  }
  return `${generatePath(productOfferingConfigsComparePath)}?${params}`;
}

export function createProductOfferingVersionHistoryUrl(productOfferingId: string, developmentId: string) {
  return generatePath(developmentPaths.productOfferingVersionHistory, { developmentId, productOfferingId });
}

export function createProjectProductOfferingVersionHistoryUrl(productOfferingId: string, projectId: string) {
  return projectPaths.projectProductOfferingVersionHistoryRoute.url({ projectId, productOfferingId });
}

type ExtractRouteParams<S extends string> = { [key in PathParam<S>]: string | undefined };
type GeneratePathParams<S extends string> = { [key in PathParam<S>]: string | null };

function generatePathWithQueryStrings<S extends string>(
  path: S,
  params: GeneratePathParams<S>,
  qs: Record<string, string | undefined>,
): string {
  const search = toParams(qs);
  return search.size ? [generatePath(path, params), search].join("?") : generatePath(path, params);
}

export function createExportCsvUrl(type: string) {
  return `${baseDownloadUrl()}/export/csv?type=${type}`;
}

export function createImportCsvUrl(type: string) {
  return `${baseDownloadUrl()}/import/csv?type=${type}`;
}

function toParams(params: Record<string, any>): URLSearchParams {
  return new URLSearchParams(
    params
      .toEntries()
      .filter(([, v]) => !!v)
      .toObject(),
  );
}

/** Builds `?projectId=…&costCodes=…&costType=…` for the bid package create flows. Keeps tagged ids (no de-tag). */
function bidPackageCreateSearch(params?: CreateBidPackageSearchParams): string {
  if (!params) return "";
  const search = new URLSearchParams();
  if (params.projectId) search.set("projectId", params.projectId);
  params.costCodes?.forEach((id) => search.append("costCodes", id));
  if (params.costType) search.set("costType", params.costType);
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createProjectProductOfferingAppliableConfigsUrl(projectId: string) {
  return projectPaths.projectProductOfferingConfigAppliableRoute.url({ projectId });
}

export function createProjectProductOfferingConfigApplyUrl(projectId: string, configId: string) {
  return projectPaths.projectProductOfferingConfigApplyRoute.url({ projectId, configId });
}
